import { User } from '@drl/core/auth/models/user.model';
import { Inject, Injectable } from '@angular/core';
import {
  InMemoryStorageService,
  StorageService,
} from 'angular-webstorage-service';

import { StoreKeys } from './store-keys.enum';
import { APPLICATION_STORE } from './store.interface';
import { PatientDirecoryFilter } from './models';

@Injectable()
export class UserSessionStoreService {
  constructor(
    @Inject(APPLICATION_STORE) private readonly store: StorageService,
    private readonly inMemoryStore: InMemoryStorageService,
  ) { }

  public saveAccessToken(token: string): boolean {
    this.store.set(StoreKeys.ACCESS_TOKEN_KEY, token);
    return true;
  }

  public getAccessToken(): string {
    return this.store.get(StoreKeys.ACCESS_TOKEN_KEY);
  }

  public removeAccessToken(): boolean {
    this.store.remove(StoreKeys.ACCESS_TOKEN_KEY);
    return true;
  }

  public saveRefreshToken(token: string): boolean {
    this.store.set(StoreKeys.REFRESH_TOKEN_KEY, token);
    return true;
  }

  public savePubnubToken(token: string): boolean {
    this.store.set(StoreKeys.PUBNUB_TOKEN_KEY, token);
    return true;
  }

  public savePasswordExpiryAlert(userEmail: string): boolean {
    this.store.set(
      StoreKeys.PasswordExpiryAlert,
      JSON.stringify({ userEmail, date: new Date() }),
    );
    return true;
  }

  public getPasswordExpiryAlert(): { userEmail; date: Date } {
    return JSON.parse(this.store.get(StoreKeys.PasswordExpiryAlert));
  }

  public getPubnubToken(): string {
    return this.store.get(StoreKeys.PUBNUB_TOKEN_KEY);
  }

  public getRefreshToken(): string {
    return this.store.get(StoreKeys.REFRESH_TOKEN_KEY);
  }

  public removeRefreshToken(): boolean {
    this.store.remove(StoreKeys.REFRESH_TOKEN_KEY);
    return true;
  }

  public setUser(user: User): void {
    this.inMemoryStore.set(StoreKeys.USER_KEY, user);
  }

  public getUser(): User {
    return this.inMemoryStore.get(StoreKeys.USER_KEY);
  }

  public setTenantName(name: string): void {
    this.store.set(StoreKeys.TENANT_KEY, name);
  }

  public getTenantName(): string {
    return this.store.get(StoreKeys.TENANT_KEY);
  }

  public setTenantKeyName(name: string): void {
    this.store.set(StoreKeys.TENANT_KEY_NAME, name);
  }

  public getTenantKeyName(): string {
    return this.store.get(StoreKeys.TENANT_KEY_NAME);
  }

  public setLocale(name: string): void {
    this.store.set(StoreKeys.LOCALE, name);
  }

  public getLocale(): string {
    return this.store.get(StoreKeys.LOCALE);
  }

  public setCountryName(name: string): void {
    this.inMemoryStore.set(StoreKeys.CountryName, name);
  }

  public getCountryName(): string {
    return this.inMemoryStore.get(StoreKeys.CountryName);
  }

  public setIsWhiteLabelled(isWhiteLabelled:boolean){
    this.inMemoryStore.set(StoreKeys.IS_WHITE_LABELLED, isWhiteLabelled);
  }

  public getIsWhiteLabelled(): boolean {
    return this.inMemoryStore.get(StoreKeys.IS_WHITE_LABELLED);
  }

  public setTenantId(id:number){
    this.inMemoryStore.set(StoreKeys.TENANT_ID, id);
  }

  public getTenantId(): number {
    return this.inMemoryStore.get(StoreKeys.TENANT_ID);
  }

  public setTenantLogo(logo: string): void {
    this.inMemoryStore.set(StoreKeys.Logo, logo);
  }

  public getTenantLogo(): string {
    return this.inMemoryStore.get(StoreKeys.Logo);
  }

  public setWhiteLabelled(isWhiteLabelled: boolean): void {
    this.inMemoryStore.set(StoreKeys.IsWhiteLabelled, isWhiteLabelled);
  }

  public getWhiteLabelled(): boolean {
    return this.inMemoryStore.get(StoreKeys.IsWhiteLabelled);
  }

  public setMonochromaticTenantLogo(monochromeLogo:string): void {
    this.inMemoryStore.set(StoreKeys.MonochromeLogo, monochromeLogo);
  }

  public getMonochromaticTenantLogo(): string {
    return this.inMemoryStore.get(StoreKeys.MonochromeLogo);
  }

  public getActiveSessionModal(): boolean {
    return this.inMemoryStore.get(StoreKeys.ACTIVE_SESSION_MODAL);
  }

  public setActiveSessionModal(value: boolean): void {
    this.inMemoryStore.set(StoreKeys.ACTIVE_SESSION_MODAL, value);
  }

  public clearAll(): void {
    this.inMemoryStore.remove(StoreKeys.USER_KEY);
    this.store.remove(StoreKeys.ACCESS_TOKEN_KEY);
    this.store.remove(StoreKeys.REFRESH_TOKEN_KEY);
    this.store.remove(StoreKeys.PUBNUB_TOKEN_KEY);
    this.store.remove(StoreKeys.PATIENT_DIR_FILTER);
  }

  public getPatientDirFilter():PatientDirecoryFilter{
    return this.store.get(StoreKeys.PATIENT_DIR_FILTER);
  }
  public setPatientDirFilter(filter: PatientDirecoryFilter){
    this.store.set(StoreKeys.PATIENT_DIR_FILTER, filter);
  }
}
